<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useConfirmAction } from 'dashboard/composables/useConfirmAction';
import PainelAPI from 'dashboard/api/painel';
import StatusChip from './StatusChip.vue';
import AuditLogList from './AuditLogList.vue';
import ConfirmActionModal from './ConfirmActionModal.vue';
import RescheduleModal from './RescheduleModal.vue';
import LinkContactModal from './LinkContactModal.vue';
import SyncStatusBadge from './SyncStatusBadge.vue';

const props = defineProps({
  appointment: { type: Object, default: null },
});
const emit = defineEmits(['close', 'updated']);

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const closeChar = '×';
const showReschedule = ref(false);
const showLink = ref(false);

const {
  state: confirmState,
  open: openConfirm,
  confirm: doConfirm,
  cancel: doCancel,
} = useConfirmAction();

watch(
  () => props.appointment?.id,
  async id => {
    if (!id) return;
    const { data } = await PainelAPI.fetchAppointment(id);
    store.commit('painelAppointments/UPSERT_APPOINTMENT', data.payload);
  },
  { immediate: true }
);

const isPending = computed(() => props.appointment?.status === 'pending');
const isConfirmed = computed(() => props.appointment?.status === 'confirmed');

const fieldDefinitions = computed(
  () => store.getters['painelFieldDefinitions/getActiveFieldDefinitions']
);

const singleLineFields = computed(() =>
  fieldDefinitions.value.filter(f => f.field_type === 'single')
);
const multiLineFields = computed(() =>
  fieldDefinitions.value.filter(f => f.field_type === 'multi')
);
const customFields = computed(() => props.appointment?.custom_fields || {});

const formatHour = iso =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const formatPhoneBR = value => {
  if (!value) return '';
  const digits = value.replace(/\D/g, '');
  const local =
    digits.length >= 12 && digits.startsWith('55') ? digits.slice(2) : digits;
  if (local.length === 11)
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10)
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return local;
};

const handleStatusAction = async actionName => {
  const result = await openConfirm({
    action: actionName,
    appointment: props.appointment,
  });
  if (!result.confirmed) return;

  const id = props.appointment.id;
  const note = result.note;

  if (actionName === 'confirm') {
    const updated = await store.dispatch('painelAppointments/update', {
      id,
      payload: { status: 'confirmed', note },
    });
    emit('updated', updated);
  } else if (actionName === 'cancel') {
    const updated = await store.dispatch('painelAppointments/update', {
      id,
      payload: { status: 'cancelled', note },
    });
    emit('updated', updated);
  } else if (actionName === 'reset') {
    const updated = await store.dispatch('painelAppointments/update', {
      id,
      payload: { status: 'pending', note },
    });
    emit('updated', updated);
  } else if (actionName === 'reschedule') {
    showReschedule.value = true;
  } else if (actionName === 'link') {
    showLink.value = true;
  }
};

const openConversation = () => {
  // eslint-disable-next-line no-underscore-dangle
  const links = props.appointment?._links;
  const conversationId =
    links?.conversation_id || props.appointment?.conversation_id;
  if (conversationId) {
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: conversationId },
    });
  } else {
    useAlert(t('PAINEL.APPOINTMENTS.NO_CONVERSATION_FOR_CONTACT'));
  }
};

const handleConfirmConfirm = note => {
  doConfirm(note);
};
</script>

<template>
  <Teleport to="body">
    <transition name="painel-slide-x">
      <aside
        v-if="appointment"
        class="fixed right-0 top-0 z-50 flex h-full w-full flex-col border-l border-n-strong bg-n-surface-2 shadow-xl sm:w-[320px]"
      >
        <header
          class="flex items-center justify-between border-b border-n-strong px-4 py-3"
        >
          <h2 class="text-sm font-semibold">
            {{ t('PAINEL.APPOINTMENTS.DETAIL.TITLE') }}
          </h2>
          <button
            class="text-n-slate-11 hover:text-n-slate-12"
            :aria-label="t('PAINEL.APPOINTMENTS.DETAIL.CLOSE')"
            @click="emit('close')"
          >
            {{ closeChar }}
          </button>
        </header>

        <div class="flex-1 space-y-4 overflow-y-auto p-4 text-sm">
          <div
            class="flex items-center gap-2 text-base font-semibold text-n-slate-12"
          >
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <span>{{
              appointment.contact?.name || appointment.title || '—'
            }}</span>
            <SyncStatusBadge :appointment="appointment" />
          </div>
          <div class="font-mono text-n-slate-11">
            {{ formatHour(appointment.starts_at) }} &#x2013;
            {{ formatHour(appointment.ends_at) }}
          </div>
          <div class="rounded border border-n-strong bg-n-surface-1 px-3 py-1">
            <div class="flex items-center justify-between py-1.5 text-xs">
              <span class="uppercase text-n-slate-10">{{
                t('PAINEL.APPOINTMENTS.SIDEBAR.STATUS')
              }}</span>
              <StatusChip :status="appointment.status" />
            </div>
            <template v-for="def in singleLineFields" :key="def.id">
              <div
                v-if="customFields[def.key]"
                class="flex items-start justify-between gap-2 border-t border-dashed border-n-weak py-1.5 text-xs"
              >
                <span class="uppercase text-n-slate-10">{{ def.label }}</span>
                <span class="text-right text-n-slate-12">{{
                  customFields[def.key]
                }}</span>
              </div>
            </template>
          </div>

          <div
            v-for="def in multiLineFields"
            v-show="customFields[def.key]"
            :key="def.id"
            class="rounded border border-n-strong bg-n-surface-1 p-3"
          >
            <div class="mb-1 text-xs uppercase text-n-slate-10">
              {{ def.label }}
            </div>
            <div class="whitespace-pre-wrap text-sm">
              {{ customFields[def.key] }}
            </div>
          </div>

          <div
            v-if="appointment.location"
            class="rounded border border-n-strong bg-n-surface-1 p-3"
          >
            <div class="mb-1 text-xs uppercase text-n-slate-10">
              {{ t('PAINEL.APPOINTMENTS.DETAIL.LOCATION') }}
            </div>
            <div class="text-sm">{{ appointment.location }}</div>
          </div>

          <div
            v-if="appointment.contact"
            class="rounded border border-n-strong bg-n-surface-1 p-3"
          >
            <div class="text-xs uppercase text-n-slate-10">
              {{ t('PAINEL.APPOINTMENTS.DETAIL.CONTACT') }}
            </div>
            <div>{{ appointment.contact.name }}</div>
            <div class="text-xs text-n-slate-11">
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              {{ formatPhoneBR(appointment.contact.phone_number) || '—' }}
            </div>
          </div>

          <div class="flex flex-wrap gap-2">
            <button
              v-if="isPending"
              class="rounded bg-n-teal-9 px-3 py-1.5 text-xs text-white"
              @click="handleStatusAction('confirm')"
            >
              {{ t('PAINEL.APPOINTMENTS.CONFIRM') }}
            </button>

            <button
              v-if="isPending || isConfirmed"
              class="rounded bg-n-ruby-9 px-3 py-1.5 text-xs text-white"
              @click="handleStatusAction('cancel')"
            >
              {{ t('PAINEL.APPOINTMENTS.CANCEL') }}
            </button>

            <button
              v-if="!isPending"
              class="rounded bg-n-slate-5 px-3 py-1.5 text-xs text-n-slate-12"
              @click="handleStatusAction('reset')"
            >
              {{ t('PAINEL.APPOINTMENTS.RESET') }}
            </button>

            <button
              class="rounded bg-n-amber-3 px-3 py-1.5 text-xs text-n-amber-12"
              @click="handleStatusAction('reschedule')"
            >
              {{ t('PAINEL.APPOINTMENTS.RESCHEDULE') }}
            </button>

            <button
              v-if="!appointment.contact"
              class="rounded bg-n-blue-3 px-3 py-1.5 text-xs text-n-blue-12"
              @click="handleStatusAction('link')"
            >
              {{ t('PAINEL.APPOINTMENTS.LINK_CONTACT') }}
            </button>

            <button
              v-if="appointment.contact"
              class="rounded bg-n-blue-3 px-3 py-1.5 text-xs text-n-blue-12"
              @click="openConversation"
            >
              {{ t('PAINEL.APPOINTMENTS.OPEN_CONVERSATION') }}
            </button>

            <a
              v-if="appointment.google_event_url"
              :href="appointment.google_event_url"
              target="_blank"
              rel="noopener noreferrer"
              class="px-2 py-1.5 text-xs text-n-slate-11 underline"
            >
              {{ t('PAINEL.APPOINTMENTS.VIEW_GOOGLE') }}
            </a>
          </div>

          <AuditLogList :logs="appointment.audit_logs || []" />
        </div>

        <RescheduleModal
          v-if="showReschedule"
          :appointment="appointment"
          @close="showReschedule = false"
        />
        <LinkContactModal
          v-if="showLink"
          :appointment="appointment"
          @close="showLink = false"
        />

        <ConfirmActionModal
          :visible="confirmState.visible"
          :action="confirmState.action"
          :appointment="confirmState.appointment"
          @confirm="handleConfirmConfirm"
          @cancel="doCancel"
        />
      </aside>
    </transition>
  </Teleport>
</template>

<style>
.painel-slide-x-enter-active,
.painel-slide-x-leave-active {
  transition: transform 200ms ease;
}
.painel-slide-x-enter-from,
.painel-slide-x-leave-to {
  transform: translateX(100%);
}
</style>
